import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ChevronLeft,
  ChevronRight,
  FileText,
  Loader2,
  RefreshCw,
  Search,
  Upload,
  X,
} from 'lucide-react';
import { SkeletonTable } from '../components/Skeleton';
import { FilterSearchInput } from '../components/FormComponents';
import SortableHeader from '../components/SortableHeader';
import { useToast } from '../components/ToastProvider';
import { useAuth } from '../context/AuthContext';
import { listReviews, submitReview } from '../features/reviews/reviewsApi';
import { ReviewStatusChip, TriageChip } from '../features/reviews/reviewBits';
import SavedViewsMenu from '../features/savedViews/SavedViewsMenu';
import { SAVED_VIEW_KEYS } from '../features/savedViews/savedViews';
import { fmtDateTimeOrEmpty as formatDateTime } from '../utils/format';
import { normalizeSearchValue, readOptionParam, readSearchParam } from '../utils/urlFilters';
import useUrlSyncedFilters, { useReseedPageGuard } from '../hooks/useUrlSyncedFilters';

const PAGE_SIZE_OPTIONS = [25, 50, 100];

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'open', label: 'Open (awaiting a decision)' },
  { value: 'pending_review', label: 'Awaiting legal' },
  { value: 'changes_requested', label: 'Changes requested' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'withdrawn', label: 'Withdrawn' },
];

const TYPE_OPTIONS = [
  { value: '', label: 'All documents' },
  { value: 'nda', label: 'NDA' },
  { value: 'other', label: 'Other' },
];

const TRIAGE_OPTIONS = [
  { value: '', label: 'All AI triage' },
  { value: 'GREEN', label: 'Green — low risk' },
  { value: 'YELLOW', label: 'Yellow — some risk' },
  { value: 'RED', label: 'Red — high risk' },
];

// The ONE definition of this list's filters given a querystring — shared by the
// mount seeding and the external-change re-seed (doc-195), so doc-194's option
// validation and doc-168's trimmed q apply identically on both paths.
function seedFromParams(params) {
  return {
    status: readOptionParam(params, 'status', STATUS_OPTIONS),
    type: readOptionParam(params, 'type', TYPE_OPTIONS),
    triage: readOptionParam(params, 'triage', TRIAGE_OPTIONS),
    search: readSearchParam(params, 'q'),
    page: parseInt(params.get('page')) || 1,
  };
}

// Identity of "what the page-reset effect is watching", for the re-seed guard.
// It must span EXACTLY that effect's dependency list — filters AND sort — or a
// change the effect reacts to produces no key change and gets swallowed
// (doc-224). Sort is not URL-synced here, so seedFromParams does not carry it
// and the re-seed call passes the live values alongside `next`.
function filtersKeyOf({ status, type, triage, search, sortField, sortOrder }) {
  return JSON.stringify([status, type, triage, search, sortField, sortOrder]);
}

export default function ReviewsList() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { canReviewRequests } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // Seeded from the URL, but only with values these selects can display
  // (doc-194) — a stale ?triage=green from an old link would otherwise filter
  // the list while the dropdown still reads "All AI triage".
  const [seed] = useState(() => seedFromParams(searchParams));
  const [status, setStatus] = useState(seed.status);
  const [type, setType] = useState(seed.type);
  const [triage, setTriage] = useState(seed.triage);
  const [search, setSearch] = useState(seed.search);
  // Sort is deliberately NOT persisted in the URL — matches ContractsList and
  // RequestsList, so all three lists behave the same on a deep link.
  const [sortField, setSortField] = useState('updatedAt');
  const [sortOrder, setSortOrder] = useState('desc');

  const [page, setPage] = useState(seed.page);
  const [pageSize, setPageSize] = useState(() => {
    const stored = localStorage.getItem('reviewsPageSize');
    return stored ? parseInt(stored) : 25;
  });
  const [pagination, setPagination] = useState({ total: 0, totalPages: 1 });
  // Bumped by the error callout's Retry button so the fetch effect re-runs
  // with identical filters — otherwise a failed load forces a hard refresh.
  const [retryToken, setRetryToken] = useState(0);

  // Reset to page 1 when a filter changes — but not on mount, or a
  // deep-linked ?page=3 gets clobbered before the first fetch; and not when the
  // change is a re-seed landing, which carries its own page.
  const { markReseeded, isReseedArrival } = useReseedPageGuard();
  const filtersTouchedRef = useRef(false);
  useEffect(() => {
    if (!filtersTouchedRef.current) {
      filtersTouchedRef.current = true;
      return;
    }
    if (isReseedArrival(filtersKeyOf({ status, type, triage, search, sortField, sortOrder }))) return;
    setPage(1);
  }, [status, type, triage, search, sortField, sortOrder, isReseedArrival]);

  // Re-seed when the querystring changes from OUTSIDE this component — the
  // sidebar "Reviews" link while already on /reviews, a CommandK jump to this
  // list. Without it the URL blanks and the controls keep their old values
  // (doc-195).
  const markUrlWritten = useUrlSyncedFilters({
    searchParams,
    onExternalChange: useCallback((params) => {
      const next = seedFromParams(params);
      // Before the state updates, so the page-reset effect recognises the
      // filter change they cause and leaves next.page alone.
      // A re-seed does not touch sort, so the key it records carries the sort
      // values that will still be live when the effect runs.
      markReseeded(filtersKeyOf({ ...next, sortField, sortOrder }));
      setStatus(next.status);
      setType(next.type);
      setTriage(next.triage);
      setSearch(next.search);
      setPage(next.page);
    }, [markReseeded, sortField, sortOrder]),
  });

  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    if (status) params.set('status', status); else params.delete('status');
    if (type) params.set('type', type); else params.delete('type');
    if (triage) params.set('triage', triage); else params.delete('triage');
    // The TRIMMED term, matching what the fetch below sends.
    const q = normalizeSearchValue(search);
    if (q) params.set('q', q); else params.delete('q');
    if (page > 1) params.set('page', page.toString()); else params.delete('page');
    setSearchParams(params, { replace: true });
    // Record what WE wrote so the re-seed above ignores our own write.
    markUrlWritten(params);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, type, triage, search, page]);

  useEffect(() => {
    // Debounced (search fires per keystroke) + stale-response guarded — a
    // slower earlier response must not paint over the latest, and a transient
    // failure must not leave the error panel up after a later success.
    let cancelled = false;
    setLoading(true);
    const timer = setTimeout(() => {
      listReviews({
        status,
        type,
        aiClassification: triage,
        q: search.trim(),
        sort: sortField,
        order: sortOrder,
        page,
        pageSize,
      })
        .then((data) => {
          if (cancelled) return;
          setReviews(data.reviews || []);
          setPagination(data.pagination || { total: 0, totalPages: 1 });
          // ?page= comes from the URL so it can be out of range, and the server
          // CLAMPS rather than 404s (document-review-service.js), reporting the
          // page it actually served. Adopt it, or a stale shared ?page=9 renders
          // "9 / 1" and "201–7 of 7" over the last page's rows (doc-183). The
          // URL-sync effect above then self-heals the querystring.
          const servedPage = data.pagination?.page;
          if (servedPage && servedPage !== page) setPage(servedPage);
          setError(null);
          setLoading(false);
        })
        .catch((err) => {
          if (cancelled) return;
          setError(err.message);
          setLoading(false);
        });
    }, 200);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [status, type, triage, search, sortField, sortOrder, page, pageSize, retryToken]);

  const handlePageSizeChange = (n) => {
    setPageSize(n);
    localStorage.setItem('reviewsPageSize', n.toString());
    setPage(1);
  };

  const handleSort = (field) => {
    if (sortField === field) setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortOrder('asc'); }
  };

  // The shared sort props, spread at each header. This used to be a local
  // <SortHeader> wrapper component declared right here in the body — but a
  // component defined inside a render gets a NEW function identity on every
  // render, so React treats it as a different element TYPE and remounts the
  // whole subtree. That destroyed the focused sort button on every sort even
  // with the table kept mounted, which is the other half of doc-230. A plain
  // props object has no identity for the reconciler to trip over.
  const sortProps = { sortField, sortOrder, onToggle: handleSort };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (file) e.target.value = '';
    if (!file) return;
    setSubmitting(true);
    try {
      const review = await submitReview(file);
      showToast('Sent to Legal for review', 'success');
      navigate(`/reviews/${review.id}`);
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const labelFor = (opts, value) => opts.find((o) => o.value === value)?.label || value;
  const clearAllFilters = () => { setStatus(''); setType(''); setTriage(''); setSearch(''); };
  const hasFilters = Boolean(status || type || triage || normalizeSearchValue(search));
  const activeFilters = [
    normalizeSearchValue(search) && {
      key: 'search',
      label: `Search: ${normalizeSearchValue(search)}`,
      onRemove: () => setSearch(''),
    },
    status && { key: 'status', label: `Status: ${labelFor(STATUS_OPTIONS, status)}`, onRemove: () => setStatus('') },
    type && { key: 'type', label: `Type: ${labelFor(TYPE_OPTIONS, type)}`, onRemove: () => setType('') },
    triage && { key: 'triage', label: `AI triage: ${labelFor(TRIAGE_OPTIONS, triage)}`, onRemove: () => setTriage('') },
  ].filter(Boolean);

  // doc-230: the skeleton is FIRST-LOAD only. Rendering it on every `loading`
  // unmounted the table — sortable headers included — on each refetch, so the
  // sort button a keyboard user had just activated was destroyed and focus fell
  // to <body>; a second Space/Enter then did nothing. A refetch over rows we
  // already have keeps the table mounted and marks it aria-busy instead.
  const firstLoad = loading && reviews.length === 0;
  const refreshing = loading && reviews.length > 0 && !error;

  return (
    <div className="canvas-inner animate-fade-in">
      <div className="page-header">
        <div>
          <h1 className="page-title">Document reviews</h1>
          <div className="page-subtitle">
            Send a contract or NDA to Legal. Every submission gets an instant AI first-pass
            (advisory) and a tracked legal sign-off.
          </div>
        </div>
        <div>
          <input ref={fileRef} type="file" accept=".pdf,.docx,.doc" onChange={onFile} style={{ display: 'none' }} />
          <button className="btn primary" disabled={submitting} onClick={() => fileRef.current?.click()}>
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            Send for review
          </button>
        </div>
      </div>

      {/* Search — own full-width row, mirroring the Requests list. */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 10, alignItems: 'center' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <FilterSearchInput
            // What CommandK's bare-`/` shortcut focuses (doc-356). Contracts
            // and Counterparties already carried it; without it here `/` fell
            // through to opening the palette on a page that has its own search.
            data-search-input
            className="filter-search-wide"
            icon={Search}
            placeholder="Search document name or requester…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        {/* Named saved views (doc-356) — the same control as the contracts
            list, on its own storage key, so a reviews view never shows up over
            there. Views here are FILTERS ONLY: sort is not URL-synced on this
            list, and a saved view is a querystring. */}
        <SavedViewsMenu storageKey={SAVED_VIEW_KEYS.reviews} hasFilters={hasFilters} />
      </div>

      {/* Filters — status/type/triage all run server-side. Option keys are
          prefix + value + index (doc-339): each list leads with an empty-valued
          "All …" placeholder, so keying on the value alone means a second
          empty-valued entry collides with it and React silently drops one. */}
      <div className="filter-row natural" style={{ marginBottom: activeFilters.length ? 10 : 16 }}>
        <select className="filter-select" value={status} onChange={(e) => setStatus(e.target.value)}>
          {STATUS_OPTIONS.map((o, i) => (
            <option key={`status-${o.value || ''}-${i}`} value={o.value}>{o.label}</option>
          ))}
        </select>
        <select className="filter-select" value={type} onChange={(e) => setType(e.target.value)}>
          {TYPE_OPTIONS.map((o, i) => (
            <option key={`type-${o.value || ''}-${i}`} value={o.value}>{o.label}</option>
          ))}
        </select>
        <select className="filter-select" value={triage} onChange={(e) => setTriage(e.target.value)}>
          {TRIAGE_OPTIONS.map((o, i) => (
            <option key={`triage-${o.value || ''}-${i}`} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>

      {/* Active filter chips */}
      {activeFilters.length > 0 && (
        <div className="active-filters" style={{ marginBottom: 16 }}>
          {activeFilters.map((f) => (
            <span key={f.key} className="active-filter-chip">
              {f.label}
              <button type="button" onClick={f.onRemove} aria-label={`Remove ${f.label}`}>
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
          <button className="btn ghost xs" onClick={clearAllFilters}>
            <X className="w-3 h-3" />
            Clear all
          </button>
        </div>
      )}

      {firstLoad ? (
        <SkeletonTable />
      ) : error ? (
        <div className="callout red">
          <div className="callout-body">
            <strong>Error loading reviews.</strong> {error}
            <div style={{ marginTop: 10 }}>
              <button
                className="btn outline sm"
                onClick={() => setRetryToken((token) => token + 1)}
              >
                <RefreshCw className="w-3.5 h-3.5" />
                Retry
              </button>
            </div>
          </div>
        </div>
      ) : reviews.length === 0 ? (
        <div className="card" style={{ padding: 40, textAlign: 'center' }}>
          <FileText className="w-7 h-7" style={{ color: 'var(--icon-tertiary)', margin: '0 auto 10px' }} />
          <div style={{ fontSize: 14, color: 'var(--text-primary)', marginBottom: 4 }}>
            {hasFilters ? 'No matching reviews' : 'No reviews yet'}
          </div>
          <div className="muted" style={{ fontSize: 12.5, marginBottom: hasFilters ? 16 : 0 }}>
            {hasFilters
              ? 'Try clearing the filters.'
              : canReviewRequests
                ? 'Documents sent to Legal — by anyone, here or in Slack — will appear here.'
                : 'Send a document to Legal and it’ll show up here with its status.'}
          </div>
          {hasFilters && (
            <button className="btn outline sm" onClick={clearAllFilters}>
              <X className="w-3.5 h-3.5" />
              Clear
            </button>
          )}
        </div>
      ) : (
        <div className="card" style={{ overflow: 'hidden' }}>
          <div
            style={{ overflowX: 'auto' }}
            className={refreshing ? 'list-refreshing' : undefined}
            aria-busy={refreshing || undefined}
          >
            <table className="tbl">
              <thead>
                <tr>
                  <SortableHeader {...sortProps} field="document" style={{ minWidth: 280 }}>Document</SortableHeader>
                  <SortableHeader {...sortProps} field="status" style={{ width: 150 }}>Status</SortableHeader>
                  <SortableHeader {...sortProps} field="aiClassification" style={{ width: 140 }}>AI triage</SortableHeader>
                  <SortableHeader {...sortProps} field="requester" style={{ width: 160 }}>Requester</SortableHeader>
                  <SortableHeader {...sortProps} field="updatedAt" style={{ width: 150 }}>Updated</SortableHeader>
                </tr>
              </thead>
              <tbody>
                {reviews.map((r) => (
                  <tr
                    key={r.id}
                    onClick={() => navigate(`/reviews/${r.id}`)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        navigate(`/reviews/${r.id}`);
                      }
                    }}
                    role="button"
                    tabIndex={0}
                  >
                    <td>
                      <div className="row" style={{ gap: 8, minWidth: 0 }}>
                        <FileText className="w-4 h-4 shrink-0" style={{ color: 'var(--icon-tertiary)' }} />
                        <span className="ellipsis" style={{ fontSize: 13, color: 'var(--text-primary)' }} title={r.filename}>
                          {r.title || r.filename || 'Document'}
                          {r.documentType === 'nda' && <span className="muted"> · NDA</span>}
                        </span>
                      </div>
                    </td>
                    <td><ReviewStatusChip status={r.status} /></td>
                    <td><TriageChip aiStatus={r.aiStatus} classification={r.aiClassification} /></td>
                    <td><span className="ellipsis" style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>{r.requesterName || '—'}</span></td>
                    <td><span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{formatDateTime(r.updatedAt)}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {pagination.total > 0 && (
            <div
              style={{
                padding: '10px 24px',
                borderTop: '0.5px solid var(--border-primary)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                fontSize: 12,
                color: 'var(--text-tertiary)',
              }}
            >
              <div className="row" style={{ gap: 16 }}>
                <span className="tnum">
                  {((page - 1) * pageSize) + 1}–{Math.min(page * pageSize, pagination.total)} of {pagination.total}
                </span>
                <div className="row" style={{ gap: 6 }}>
                  <span>Per page</span>
                  <select
                    className="filter-select"
                    value={pageSize}
                    onChange={(e) => handlePageSizeChange(parseInt(e.target.value))}
                    style={{ height: 24, padding: '0 22px 0 8px', fontSize: 11 }}
                  >
                    {PAGE_SIZE_OPTIONS.map((size) => (
                      <option key={size} value={size}>{size}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="row" style={{ gap: 4 }}>
                <button
                  className="icon-btn xs"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  aria-label="Previous page"
                  style={{ opacity: page === 1 ? 0.4 : 1 }}
                >
                  <ChevronLeft className="w-3.5 h-3.5" />
                </button>
                <span className="mono tnum" style={{ minWidth: 56, textAlign: 'center' }}>
                  {page} / {pagination.totalPages}
                </span>
                <button
                  className="icon-btn xs"
                  onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
                  disabled={page === pagination.totalPages}
                  aria-label="Next page"
                  style={{ opacity: page === pagination.totalPages ? 0.4 : 1 }}
                >
                  <ChevronRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
